import { countdownStep } from "../shared/countdown";
import { playMatchWhistle } from "./effects";

type CountdownCallbacks = {
  onStep: (step: number) => void;
  onGo: () => void;
  onHide: () => void;
  now?: () => number;
};

/** One disposable sequence per round: state updates never replay GO or the whistle. */
export class RoundCountdown {
  private key: string | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private readonly now: () => number;

  constructor(private readonly callbacks: CountdownCallbacks) {
    this.now = callbacks.now ?? (() => performance.now());
  }

  sync(key: string | null, deadline = 0): void {
    if (key === this.key) return;
    this.cancel();
    this.key = key;
    // Reconnecting after the start must not replay a countdown or a late whistle.
    if (key === null || deadline <= this.now()) return;
    let lastStep = -1;
    const tick = () => {
      const remaining = deadline - this.now();
      const step = countdownStep(deadline, this.now());
      if (step !== lastStep) {
        this.callbacks.onStep(step);
        lastStep = step;
      }
      if (step === 0) {
        this.callbacks.onGo();
        this.timer = setTimeout(() => this.callbacks.onHide(), 440);
      } else {
        // Schedule against the deadline, so background timer throttling cannot drift it.
        this.timer = setTimeout(tick, Math.max(1, remaining - (step - 1) * 1_000));
      }
    };
    tick();
  }

  cancel(): void {
    clearTimeout(this.timer);
    this.timer = undefined;
    this.key = null;
    this.callbacks.onHide();
  }
}

export function createRoundCountdown(onGo: () => void): RoundCountdown {
  let overlay: HTMLDivElement | null = null;
  return new RoundCountdown({
    onStep(step) {
      if (!overlay) {
        overlay = document.createElement("div");
        overlay.className = "match-countdown";
        overlay.setAttribute("role", "status");
        overlay.setAttribute("aria-live", "assertive");
        overlay.setAttribute("aria-atomic", "true");
        document.body.append(overlay);
      }
      overlay.dataset.step = String(step);
      overlay.innerHTML = `<span class="countdown-ring" aria-hidden="true"></span><strong>${step || "GO!"}</strong>`;
    },
    onGo() {
      playMatchWhistle();
      onGo();
    },
    onHide() {
      overlay?.remove();
      overlay = null;
    },
  });
}
